// ============================================================
// 데이터셋 카탈로그 행 가져오기(저작 시점 박제) — 순수 변환 층
// ============================================================
//
// 엑셀 가져오기(datasetExcelImport)의 카탈로그 판이다. 저작자가 **지금** 카탈로그를 조회해
// 그 행들을 직접 입력 데이터셋의 기본행(defaultRows)으로 박아 둔다 — 이후 카탈로그가 바뀌어도
// 문서는 그대로다(동적 축인 dataDef.prefillFrom은 매 작성 시점에 읽는다는 점에서 정반대).
//
// 원천이 문자열 격자(엑셀)가 아니라 **이미 타입을 가진 행 객체**(서버 resolve 결과)라는 점만
// 다르고, "어느 원천 필드가 어느 데이터셋 칼럼에 들어가는가 / 값을 칼럼 위젯 규약에 맞게 어떻게
// 저장하는가"의 규율은 엑셀과 같다 — 변환의 문자열 경로는 convertCell 정본을 그대로 태운다
// (두 벌 규칙 금지). 모달(DatasetCatalogImportModal)은 이 함수들의 결과를 그리기만 한다.

import type { CatalogEntry, CatalogField, DataDefField } from '@safetyhub/shared';
import {
  buildColumnDefaults,
  convertCell,
  normalizeHeaderLabel,
  type CellConversion,
  type ColumnDefaults,
  type DefaultValueWarning,
  type ImportWarning,
  type OptionLookup,
} from './datasetExcelImport';

/** 카탈로그 행의 값 원천 후보 — 행 객체 안 경로(점 표기 가능) + 표시 라벨. */
export interface CatalogRowField {
  path: string;
  label: string;
}

/** 데이터셋 칼럼 key → 원천 경로. null = 미매핑(기본값으로 채움). */
export type CatalogColumnMapping = Record<string, string | null>;

/** 관측 키 수집에 살필 최대 행 수 — 행마다 키가 다른 경우는 드물고, 전 행 스캔은 비용만 든다. */
const OBSERVED_ROW_SAMPLE_LIMIT = 50;

/**
 * 행 객체에서 점 표기 경로로 값을 읽는다(예: 'riskEstimation.severity').
 *
 * 선언된 반환 필드의 path가 중첩 경로일 수 있어 최상위 키 직접 조회로는 부족하다.
 * 배열 인덱스·필터 같은 고급 경로는 다루지 않는다 — 후보 목록(collectCatalogRowFields)이
 * 내놓는 경로만 감당하면 된다.
 */
export function getCatalogRowValue(row: Record<string, unknown>, path: string): unknown {
  let current: unknown = row;
  for (const segment of path.split('.')) {
    if (current === null || typeof current !== 'object' || Array.isArray(current)) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

/**
 * 매핑 후보(원천 필드) 목록 — **선언(카탈로그 returns) 우선 + 관측(실제 행 키) 보강**.
 *
 * - `cardinality:'list'` entry는 returns가 곧 행의 필드 명세다.
 * - 단수 entry는 select가 가리킨 배열 반환 필드의 `fields`가 행 명세다(datasetRowsReturnPaths가
 *   내놓는 top-level 배열 경로와 짝).
 * - 선언에 없는데 실제 행에 있는 키(서버가 파생 부여한 display 등)는 뒤에 덧붙인다 —
 *   선언만 믿으면 실데이터가 주는 값을 고를 수 없고, 관측만 믿으면 빈 결과에서 후보가 0이 된다.
 *
 * object/array 타입 필드도 후보에 남긴다(고르면 JSON 문자열 변환 + 경고) — 숨기면 "값이
 * 있는데 고를 수 없는" 침묵 결핍이 된다.
 */
export function collectCatalogRowFields(
  entry: CatalogEntry | undefined,
  select: string | undefined,
  rows: readonly Record<string, unknown>[],
): CatalogRowField[] {
  const declared: CatalogField[] =
    entry === undefined
      ? []
      : entry.cardinality === 'list'
        ? entry.returns
        : (entry.returns.find((r) => r.path === select)?.fields ?? []);

  const out: CatalogRowField[] = declared.map((f) => ({ path: f.path, label: f.label }));
  const known = new Set(out.map((f) => f.path));

  for (const row of rows.slice(0, OBSERVED_ROW_SAMPLE_LIMIT)) {
    for (const key of Object.keys(row)) {
      if (known.has(key)) continue;
      known.add(key);
      out.push({ path: key, label: key });
    }
  }
  return out;
}

/** 경고·미리보기용 원문 표시 — 객체/배열은 JSON, 그 외는 String. null/undefined는 빈 문자열. */
export function catalogValueDisplay(raw: unknown): string {
  if (raw === null || raw === undefined) return '';
  if (typeof raw === 'object') {
    try {
      return JSON.stringify(raw);
    } catch {
      return String(raw);
    }
  }
  return String(raw);
}

/**
 * 카탈로그 값 → 칼럼 위젯에 맞는 저장 값.
 *
 * 원천이 이미 타입을 갖고 있어 **일치하는 타입은 그대로 통과**시킨다(checkbox←boolean,
 * number←number). 그 외에는 문자열로 접어 엑셀 셀과 같은 변환(convertCell)을 태운다 —
 * 표기 판정(체크 표기·숫자 표기·선택지 라벨 매칭)의 정본을 두 벌로 만들지 않기 위해서다.
 * 객체/배열은 JSON 문자열로 넣고 경고한다(침묵 손실 금지 — 무엇이 접혔는지 보여야 고친다).
 */
export function convertCatalogValue(
  raw: unknown,
  field: DataDefField,
  lookup?: OptionLookup,
): CellConversion {
  if (raw === null || raw === undefined) {
    // 빈 값 규약은 convertCell의 빈 문자열과 같다(checkbox는 해제, 그 외는 '').
    return convertCell('', field, lookup);
  }
  if (field.widget === 'checkbox' && typeof raw === 'boolean') return { value: raw };
  if (field.widget === 'number' && typeof raw === 'number' && Number.isFinite(raw)) {
    return { value: raw };
  }
  if (typeof raw === 'object') {
    return {
      value: catalogValueDisplay(raw),
      warning: '목록/묶음 값이라 JSON 문자열로 넣었습니다.',
    };
  }
  return convertCell(String(raw), field, lookup);
}

/**
 * 데이터셋 칼럼 ← 카탈로그 원천 필드 자동 매칭(라벨/키 동일 시).
 *
 * 엑셀 autoMatchColumns와 같은 규율: 칼럼의 label→key 순으로 후보를 만들고, 원천의
 * label·path(정규화 비교)와 맞춰 본다. 한 원천 필드가 두 칼럼에 붙지 않도록 먼저 잡은 쪽이
 * 차지한다. path는 마지막 조각으로도 맞춰 본다 — 중첩 경로('riskEstimation.severity')의
 * 칼럼 key는 보통 끝 조각('severity')과 일치한다.
 */
export function autoMatchCatalogColumns(
  fields: readonly Pick<DataDefField, 'key' | 'label'>[],
  sourceFields: readonly CatalogRowField[],
): CatalogColumnMapping {
  const normalized = sourceFields.map((s) => {
    const segments = s.path.split('.');
    return {
      path: s.path,
      label: normalizeHeaderLabel(s.label),
      pathKey: normalizeHeaderLabel(s.path),
      lastSegment: normalizeHeaderLabel(segments[segments.length - 1] ?? ''),
    };
  });
  const used = new Set<string>();
  const mapping: CatalogColumnMapping = {};
  for (const field of fields) {
    const candidates = [field.label, field.key]
      .map((s) => normalizeHeaderLabel(s ?? ''))
      .filter((s) => s.length > 0);
    let matched: string | null = null;
    for (const candidate of candidates) {
      const hit = normalized.find(
        (s) =>
          !used.has(s.path) &&
          ((s.label.length > 0 && s.label === candidate) ||
            (s.pathKey.length > 0 && s.pathKey === candidate) ||
            (s.lastSegment.length > 0 && s.lastSegment === candidate)),
      );
      if (hit) {
        matched = hit.path;
        break;
      }
    }
    if (matched !== null) used.add(matched);
    mapping[field.key] = matched;
  }
  return mapping;
}

export interface BuildCatalogRowsParams {
  fields: readonly DataDefField[];
  /** 서버 resolve로 받은 카탈로그 행 원본(조회 시점 값). */
  sourceRows: readonly Record<string, unknown>[];
  mapping: CatalogColumnMapping;
  /** 미매핑 칼럼에 채울 기본값(원문). 비어 있으면 빈 값. */
  defaults?: ColumnDefaults;
}

export interface BuildCatalogRowsResult {
  rows: Record<string, unknown>[];
  warnings: ImportWarning[];
  /** 미매핑 칼럼 기본값이 그대로 저장되지 못한 경우 — 칼럼당 1건. */
  defaultWarnings: DefaultValueWarning[];
}

/**
 * 매핑대로 기본행을 만든다(박제 대상 값).
 *
 * - 엑셀과 달리 **행을 건너뛰지 않는다** — 카탈로그 행은 서식 잔재가 아니라 전부 실데이터다.
 *   조회 결과가 곧 가져올 행 수다(모달이 반영 전 총수를 그대로 보여 준다).
 * - 결과 행은 항상 `fields`의 키를 모두 갖는다(빠진 키는 렌더 빈칸 + 이후 편집 키 어긋남).
 * - 경고의 rowNumber는 조회 결과 기준 1-based다(모달 미리보기 표의 줄 번호와 일치).
 */
export function buildRowsFromCatalog({
  fields,
  sourceRows,
  mapping,
  defaults = {},
}: BuildCatalogRowsParams): BuildCatalogRowsResult {
  const rows: Record<string, unknown>[] = [];
  const warnings: ImportWarning[] = [];

  // 선택지 조회표·기본값 변환은 칼럼당 한 번 — 엑셀 정본(buildColumnDefaults) 공용 호출.
  const { optionLookups, defaultValues, defaultWarnings } = buildColumnDefaults(
    fields,
    mapping,
    defaults,
  );

  sourceRows.forEach((sourceRow, ri) => {
    const row: Record<string, unknown> = {};
    fields.forEach((field, fi) => {
      const sourcePath = mapping[field.key];
      if (sourcePath === null || sourcePath === undefined) {
        row[field.key] = defaultValues.get(field.key) ?? '';
        return;
      }
      const raw = getCatalogRowValue(sourceRow, sourcePath);
      const conversion = convertCatalogValue(raw, field, optionLookups[fi]);
      row[field.key] = conversion.value;
      if (conversion.warning) {
        warnings.push({
          rowNumber: ri + 1,
          fieldKey: field.key,
          fieldLabel: field.label || field.key,
          raw: catalogValueDisplay(raw),
          message: conversion.warning,
        });
      }
    });
    rows.push(row);
  });

  return { rows, warnings, defaultWarnings };
}
